import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { pewterPanelApproved } from "@majourneys/booking";
import {
  onAnalyticsConsent,
  readAnalyticsConsent,
  writeAnalyticsConsent,
  type AnalyticsConsent,
} from "../consent/analytics-consent";
import { loadAdPixelsIfConsented } from "../consent/ad-pixels-loader";
import { loadClarityIfConsented } from "../consent/clarity-loader";
import { loadPostHogIfConsented } from "../analytics/posthog";

/**
 * `CARD_FILL` at 92%, rather than the constant itself.
 *
 * The card's fill is fully opaque, which is right for a card sitting on the
 * page and wrong for a bar the page scrolls underneath: an opaque fill makes
 * the `backdrop-blur` behind it do nothing at all. Same three stops, same
 * cool off-white, just let through.
 */
const BANNER_FILL =
  "linear-gradient(180deg, rgba(252,252,254,0.92) 0%, rgba(246,246,251,0.92) 62%, rgba(242,242,248,0.92) 100%)";

/**
 * Explicit-consent cookie banner. Renders nothing on the server and on the
 * first client paint (the `visible` flag flips in an effect), so it never
 * causes a hydration mismatch and never flashes for visitors who already
 * decided. The decision is remembered per-device in localStorage; analytics
 * (Microsoft Clarity, PostHog) and the ad pixels (Meta, TikTok, Google Ads
 * storage) only start after "Accept": see consent/clarity-loader.ts,
 * consent/ad-pixels-loader.ts and analytics/posthog.ts.
 */
export function CookieConsentBanner() {
  const { t } = useTranslation();
  const { lang } = useParams({ strict: false }) as unknown as { lang?: string };
  const locale = lang ?? "en";
  const [visible, setVisible] = useState(false);
  const [wide, setWide] = useState(false);

  useEffect(() => {
    loadClarityIfConsented();
    loadPostHogIfConsented();
    loadAdPixelsIfConsented();
    if (readAnalyticsConsent() === null) setVisible(true);
    // Answered somewhere else (the cookie question above a booking's send
    // button): the banner has nothing left to ask.
    return onAnalyticsConsent(() => setVisible(false));
  }, []);

  /*
   * Below lg the bar used to sit above the bottom menu as a full-width strip,
   * text on one row and the buttons stacked under it: about a fifth of a
   * phone screen, over the Reserve bar on activity pages (owner, 2026-09-30:
   * ship "a smaller cookie bar on phones"). Phones now get a slim floating
   * card pinned under the header instead. Read here rather than with CSS
   * classes because the card takes the pewter panel's inline style, and the
   * banner only renders after this effect anyway, so there is no hydration
   * gap to worry about.
   */
  useEffect(() => {
    const mq = window.matchMedia?.("(min-width: 1024px)");
    if (!mq) return;
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  if (!visible) return null;

  function decide(value: AnalyticsConsent) {
    writeAnalyticsConsent(value);
    setVisible(false);
  }

  const message = t("cookie_banner.message", {
    defaultValue: "This site uses cookies to improve your experience.",
  });

  const learnMore = (
    <a
      href={`/${locale}/cookie-policy`}
      className="font-semibold text-[#110E39] underline underline-offset-2 transition hover:text-[#040033]"
    >
      {t("cookie_banner.learn_more", { defaultValue: "Learn more" })}
    </a>
  );
  const declineLabel = t("cookie_banner.decline", { defaultValue: "Essential only" });
  const acceptLabel = t("cookie_banner.accept", { defaultValue: "Accept" });
  const regionLabel = t("cookie_banner.notice_label", { defaultValue: "Cookie notice" });

  if (!wide) {
    return (
      <div
        role="region"
        aria-label={regionLabel}
        /*
          A card with four corners, so it takes the full pewter panel, band
          and all. `--mj-header-h` is the header's measured height (0 where
          the header is hidden, as on phone activity pages), so the card
          always lands just under whatever is pinned at the top.
        */
        className="fixed inset-x-3 top-[calc(var(--mj-header-h,68px)+8px)] z-[900] mx-auto max-w-[520px] px-3.5 py-2.5 text-[13px] leading-[18px] text-[#5a5d77]"
        style={{
          ...pewterPanelApproved(20),
          boxShadow: `${pewterPanelApproved(20).boxShadow}, 0 14px 30px -14px rgba(17,14,57,0.35)`,
        }}
      >
        <p className="text-start">
          {message} {learnMore}
        </p>
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="inline-flex min-h-8 items-center justify-center rounded-full border border-[rgba(17,14,57,0.16)] bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-[#110E39]"
          >
            {declineLabel}
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="inline-flex min-h-8 items-center justify-center rounded-full bg-gradient-to-br from-[#2E2878] via-[#1A1454] to-[#110E39] px-4 py-1.5 text-[12.5px] font-semibold text-white"
          >
            {acceptLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label={regionLabel}
      /*
        The pewter material without the pewter frame (owner, 2026-09-07). The
        band belongs to something with four corners; this bar is full-bleed and
        pinned to the bottom, so a band would only ever show two of its sides
        and read as a stray line. What carries over is the surface: the sheet's
        cool off-white rather than flat white, the panel's own hairline at
        `rgba(17,14,57,0.13)` on the one edge that faces the page, and the
        panel shadow rather than a heavier drop.

        `backdrop-blur` earns its place because the fill is let through at 92%,
        so the page reads softly under the bar as it scrolls past.
      */
      data-mj-cookie-banner
      className="fixed inset-x-0 bottom-0 z-[900] pb-[env(safe-area-inset-bottom,0px)] border-t border-[rgba(17,14,57,0.13)] shadow-[0_-8px_30px_rgba(17,14,57,0.06)] backdrop-blur-xl"
      style={{ background: BANNER_FILL }}
    >
      <div className="mx-auto flex w-full max-w-[1100px] flex-col items-center gap-3 px-5 py-3 text-[14px] leading-[20px] text-[#5a5d77] sm:flex-row sm:justify-between">
        <p className="text-start">
          {message} {learnMore}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="inline-flex min-h-9 items-center justify-center rounded-full border border-[rgba(17,14,57,0.16)] bg-white px-5 py-2 text-[13px] font-semibold text-[#110E39] transition hover:border-[rgba(17,14,57,0.32)]"
          >
            {declineLabel}
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="inline-flex min-h-9 items-center justify-center rounded-full bg-gradient-to-br from-[#2E2878] via-[#1A1454] to-[#110E39] px-5 py-2 text-[13px] font-semibold text-white shadow-[0_10px_24px_-12px_rgba(17,14,57,0.65)] transition hover:opacity-95"
          >
            {acceptLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
